/**
 * The per-loan readiness blocks (closing-readiness.md §3), rendered from the
 * full `GET /closing/readiness/{dealId}` object. Used by the calendar detail
 * drawer (`compact`) and the drilldown Closing tab (full).
 *
 * Order follows the closer's reading order: what the pipeline says → why the
 * level is what it is → what's missing to schedule → what's gating funding →
 * conditions / exceptions / documents / lock → date churn → AI headline.
 *
 * `resolveDoc(id)` returns the FULL loan-document object for a readiness
 * document row (the preview sidebar needs the whole doc, not the summary row);
 * a row whose doc can't be resolved renders without a click affordance.
 */
import { createContext, useContext, useEffect, useMemo, useState } from "react";

import AddRounded from "@mui/icons-material/AddRounded";
import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import EditOutlined from "@mui/icons-material/EditOutlined";
import BusinessOutlined from "@mui/icons-material/BusinessOutlined";
import CheckCircleOutlineRounded from "@mui/icons-material/CheckCircleOutlineRounded";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import FileUploadOutlined from "@mui/icons-material/FileUploadOutlined";
import HomeWorkOutlined from "@mui/icons-material/HomeWorkOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowRightRounded from "@mui/icons-material/KeyboardArrowRightRounded";
import LockRounded from "@mui/icons-material/LockRounded";
import RadioButtonUncheckedRounded from "@mui/icons-material/RadioButtonUncheckedRounded";
import ScheduleRounded from "@mui/icons-material/ScheduleRounded";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import {
  Box,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";

import { formatDate } from "../../_src_shared/utils/functions/formatDate";
import { formatDateTime } from "../../_src_shared/utils/functions/formatDateTime";
import { amber, slate, status, steel } from "../../theme/colors";
import Flx from "../layout/Flx";
import Txt from "../typography/Txt";
import {
  AttentionChips,
  Muted,
  Pill,
  ReadinessBadge,
} from "./ReadinessBits";
import {
  docStatusMeta,
  eventNoun,
  firmDateLabel,
  gateLabel,
  gatingKindMeta,
  isTrade,
  pipelineStatusLabel,
  readinessMeta,
  scheduleFieldDestination,
} from "./closingReadinessUtils";
import MissingDocUploadModal from "./MissingDocUploadModal";
import { ScheduleFieldDialog, scheduleWritableFields } from "./ScheduleEditForm";
import { addressLabel } from "../../_src_shared/utils/functions/addressLabel";
import {
  buildValidityModel,
  dayKey,
} from "../../screens/LoanDrilldownScreen/tabs/LoanDrilldownDocumentsTab/docValidityModel";
import { FONT_MONO, INK, MUTED as MUTED_INK, SLATE, TYPE } from "./typography";

const MONO = FONT_MONO;

// ── Rails ───────────────────────────────────────────────────────────────────
// Nested content is spaced by LEVEL, not per element: R1 is the accordion
// header AND every direct child of its body (helper line, rows, sub-group
// headers) — the header's chevron and the rows' state glyphs share one left
// edge; R2 rows inside a sub-group step in by `step`. Two rails is the ceiling
// — anything deeper gets regrouped.
const RAIL = { pad: 14, step: 20 };
const R1 = `${RAIL.pad}px`;
const R2_STEP = `${RAIL.step}px`; // added inside a sub-group

const Block = ({ children, sx, id }) => (
  <Box
    id={id}
    sx={{
      py: 1.75,
      borderTop: `1px solid ${slate[100]}`,
      "&:first-of-type": { borderTop: "none", pt: 0 },
      ...sx,
    }}
  >
    {children}
  </Box>
);

// The sub-sections are a stack of separate cards (one `Collapsible` each),
// 9px apart, so a collapsed panel reads as a list of status rows.
const BlockGroup = ({ children, sx }) => (
  <Flx column sx={{ gap: "9px", ...sx }}>
    {children}
  </Flx>
);

// "Collapse all / Expand all" broadcast. A host (the drawer, the Closing tab)
// bumps `version` with the desired `open`; every Collapsible / drawer Section
// under the provider snaps to it, then goes back to toggling on its own.
export const CollapseAllContext = createContext(null);

// The hook a foldable section uses: local open state that also obeys the
// broadcast. An explicit `defaultOpen` wins; otherwise the host's
// `defaultOpen` (from useCollapseAllState) seeds it; otherwise open. A section
// mounted with an `id` also opens when the host focuses that id
// (`focusSection(id)` — the deep links from the blocker callout).
export const useCollapsible = (defaultOpen, id) => {
  const ctx = useContext(CollapseAllContext);
  const [open, setOpen] = useState(defaultOpen ?? ctx?.defaultOpen ?? true);
  const version = ctx?.version;
  const wanted = ctx?.open;
  useEffect(() => {
    if (version) setOpen(!!wanted);
  }, [version, wanted]);
  const focusId = ctx?.focus?.id;
  const focusVersion = ctx?.focus?.version;
  useEffect(() => {
    if (id && focusVersion && focusId === id) setOpen(true);
  }, [id, focusId, focusVersion]);
  return [open, setOpen];
};

// The section ids the hosts deep-link to (`focusSection(id)` + scrollIntoView).
export const SECTION_ID = {
  scheduling: "readiness-scheduling",
  documents: "readiness-documents",
};

// A titled card the reader can fold: chevron on the left, title, then the
// `right` slot (counts, pills) which stays clickable without toggling. The
// body sits on a faint ground under a hairline. `nested` = the body holds its
// own sub-sections (groups, cards): only a whisper of top padding.
const Collapsible = ({ title, right, defaultOpen, nested, children, sx, id }) => {
  const [open, setOpen] = useCollapsible(defaultOpen, id);
  return (
    <Box
      id={id}
      sx={{
        border: `1px solid ${slate[200]}`,
        borderRadius: "10px",
        overflow: "hidden",
        background: "#fff",
        ...sx,
      }}
    >
      <Flx
        ac
        g={1.125}
        onClick={() => setOpen((o) => !o)}
        sx={{
          px: R1,
          py: 1.5,
          cursor: "pointer",
          userSelect: "none",
          transition: "background 0.12s",
          "&:hover": { background: slate[50] },
        }}
      >
        <KeyboardArrowRightRounded
          sx={{
            fontSize: 16,
            color: slate[400],
            flexShrink: 0,
            transition: "transform 0.18s",
            transform: open ? "rotate(90deg)" : "none",
          }}
        />
        <Txt sx={{ ...TYPE.accordionH, minWidth: 0, flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {title}
        </Txt>
        {right != null && (
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              flexShrink: 0,
              // Every chip / count in a header row shares the mono stack.
              fontFamily: MONO,
              "& *": { fontFamily: MONO },
            }}
          >
            {right}
          </Box>
        )}
      </Flx>
      {open && (
        <Box
          sx={{
            px: R1,
            pt: nested ? 1.25 : "6px",
            pb: 1.5,
            borderTop: `1px solid ${slate[100]}`,
            background: slate[50],
          }}
        >
          {children}
        </Box>
      )}
    </Box>
  );
};

// "0/7 accepted" — the fraction in ink, the noun muted.
const Metric = ({ n, label }) => (
  <Txt sx={{ ...TYPE.meta, color: SLATE, whiteSpace: "nowrap" }}>
    <Box component="span" sx={{ color: INK, fontWeight: 600 }}>
      {n}
    </Box>{" "}
    {label}
  </Txt>
);

const Count = ({ n, tone = "neutral" }) => {
  const m =
    tone === "bad"
      ? { c: status.error.fg, bg: status.error.bg }
      : tone === "warn"
        ? { c: amber.chipText, bg: amber.light }
        : tone === "good"
          ? { c: status.success.fg, bg: status.success.bg }
          : { c: slate[600], bg: slate[100] };
  return <Pill label={String(n)} c={m.c} bg={m.bg} size="xs" />;
};

// The alert sentence ("Missing scheduling entries: closingScheduled, …") — the
// raw field names inside it are machine values, so they get the mono tag.
const FIELD_NAME = /\b[a-z]+(?:[A-Z][a-z0-9]+)+\b/g;
const renderReason = (text) => {
  const s = String(text || "");
  const parts = [];
  let last = 0;
  let m;
  FIELD_NAME.lastIndex = 0;
  while ((m = FIELD_NAME.exec(s))) {
    if (m.index > last) parts.push(s.slice(last, m.index));
    parts.push(
      <Box component="span" key={m.index} sx={{ ...TYPE.monoTag, color: SLATE }}>
        {m[0]}
      </Box>,
    );
    last = m.index + m[0].length;
  }
  if (last < s.length) parts.push(s.slice(last));
  return parts;
};

const ago = (days) =>
  days == null ? null : days === 0 ? "today" : days === 1 ? "1 day ago" : `${days} days ago`;

// ── 1. Status + level + reasons ──────────────────────────────────────────────
export const ReadinessHeader = ({ readiness, compact }) => {
  const m = readinessMeta(readiness?.readinessLevel);
  return (
    <Block>
      <Flx ac jb wrap g={1}>
        <Txt sx={TYPE.status}>
          {pipelineStatusLabel(readiness)}
        </Txt>
        <ReadinessBadge
          level={readiness?.readinessLevel}
          signal={readiness?.fundingSignal}
        />
      </Flx>
      <Flx ac g={1} sx={{ mt: 0.5 }}>
        <Muted>
          {eventNoun(readiness)} ·{" "}
          {readiness?.fundingReady ? "ready to fund" : "not yet ready to fund"}
        </Muted>
      </Flx>
      <Box sx={{ mt: 1 }}>
        <AttentionChips card={readiness} size="xs" />
      </Box>
      {Array.isArray(readiness?.reasons) && readiness.reasons.length > 0 && (
        <Flx column g={0.5} sx={{ mt: 1.25 }}>
          {readiness.reasons.map((r, i) => (
            <Flx key={i} g={0.75} sx={{ alignItems: "flex-start" }}>
              <Box
                sx={{ width: 6, height: 6, borderRadius: "99px", background: m.c, mt: "6px", flexShrink: 0 }}
              />
              <Txt sx={{ ...TYPE.body, fontWeight: 450 }}>{renderReason(r)}</Txt>
            </Flx>
          ))}
        </Flx>
      )}
    </Block>
  );
};

// ── Checklist row (shared by scheduling / gating / conditions) ───────────────
// A flat list row: state ring · title (one line, truncated) · optional right
// pill · optional chevron when there's a longer `detail` to expand. Rows are
// separated by hairlines, not boxed. `tone`:
//   open    amber ring (an item still to clear)
//   done    green check
//   missing red ring (a required entry that isn't there)
//   hard    red warning (a hard stop)
//   muted   grey ring (informational / optional)
const ROW_TONES = {
  open: { icon: RadioButtonUncheckedRounded, c: amber.main },
  done: { icon: CheckCircleOutlineRounded, c: status.success.solid },
  missing: { icon: RadioButtonUncheckedRounded, c: status.error.solid },
  hard: { icon: WarningAmberOutlined, c: status.error.solid },
  muted: { icon: RadioButtonUncheckedRounded, c: slate[300] },
};

// `onClick` (rows without a `detail`) makes the whole row an action — the
// Scheduling entries edit menu; `active` holds the hover fill while it's open.
export const ChecklistRow = ({
  tone = "open",
  title,
  sub,
  detail,
  pills,
  right,
  strike,
  defaultOpen = false,
  onClick,
  active = false,
  actionLabel,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const t = ROW_TONES[tone] || ROW_TONES.open;
  const Icon = t.icon;
  const expandable = !!detail;
  const actionable = !expandable && typeof onClick === "function";
  return (
    <Box>
      <Flx
        ac
        g={1.125}
        onClick={expandable ? () => setOpen((o) => !o) : actionable ? onClick : undefined}
        role={actionable ? "button" : undefined}
        tabIndex={actionable ? 0 : undefined}
        aria-label={actionable ? actionLabel : undefined}
        onKeyDown={
          actionable
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onClick(e);
                }
              }
            : undefined
        }
        sx={{
          py: "7px",
          px: "2px",
          borderRadius: "6px",
          cursor: expandable || actionable ? "pointer" : "default",
          userSelect: expandable || actionable ? "none" : "auto",
          transition: "background 0.12s",
          background: active ? slate[100] : undefined,
          "&:hover": { background: slate[100] },
          "&:focus-visible": { outline: `2px solid ${slate[300]}`, outlineOffset: 1 },
          // The pencil only surfaces on hover / focus / while the menu is open.
          "& .row-action": { opacity: active ? 1 : 0, transition: "opacity 0.12s" },
          "&:hover .row-action, &:focus-visible .row-action": { opacity: 1 },
        }}
      >
        {/* Glyph left-edged to the rail so every child of the section — helper
            line, empty-state icon, toggles, row glyphs — shares one left edge. */}
        <Icon sx={{ fontSize: 15, color: t.c, flexShrink: 0 }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Txt
            sx={{
              // type/row; blocking rows escalate in weight + red, done rows recede.
              ...(tone === "done" ? TYPE.rowDone : tone === "missing" || tone === "hard" ? TYPE.rowBlocking : TYPE.row),
              textDecoration: strike ? "line-through" : "none",
              display: "block",
              whiteSpace: open ? "normal" : "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {title}
          </Txt>
          {sub && (
            <Muted
              sx={{
                display: "block",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {sub}
            </Muted>
          )}
        </Box>
        {/* The value slot: entered value / owner · age, then the tags. */}
        <Flx ac sx={{ gap: "5px", flexShrink: 0 }}>
          {right}
          {Array.isArray(pills) &&
            pills.filter(Boolean).map((p, i) => (
              <Pill key={i} variant="tag" label={p.label} c={p.c} bg={p.bg} size="xs" title={p.title} />
            ))}
        </Flx>
        {actionable && (
          <EditOutlined className="row-action" sx={{ fontSize: 14, color: slate[400], flexShrink: 0 }} />
        )}
        {expandable && (
          <KeyboardArrowDownRounded
            sx={{
              fontSize: 17,
              color: slate[400],
              flexShrink: 0,
              transition: "transform 0.15s",
              transform: open ? "rotate(180deg)" : "none",
            }}
          />
        )}
      </Flx>
      {expandable && open && (
        <Box
          sx={{
            mb: 1,
            // Secondary text hangs under the title's left edge (glyph 15 + gap 9).
            ml: "26px",
            px: 1.5,
            py: 1.1,
            borderRadius: "8px",
            background: "#fff",
            border: `1px solid ${slate[200]}`,
            // type/detail — never smaller than the row it expands from.
            ...TYPE.detail,
            whiteSpace: "pre-wrap",
          }}
        >
          {detail}
        </Box>
      )}
    </Box>
  );
};

// Right-side muted meta text on a row (value, owner · age, cleared-by…) —
// the same slot Scheduling entries use for the entered value.
const RowMeta = ({ text }) =>
  text ? (
    <Txt sx={{ ...TYPE.meta, color: SLATE, whiteSpace: "nowrap", flexShrink: 0 }}>{text}</Txt>
  ) : null;

// The muted summary line under a checklist section's header.
const SummaryLine = ({ parts }) => {
  const list = (parts || []).filter(Boolean);
  if (!list.length) return null;
  return (
    <Muted sx={{ display: "block", pt: "6px", pb: 1, px: "2px" }}>
      {list.map((p, i) => (
        <Box component="span" key={i}>
          {i > 0 && " · "}
          {p}
        </Box>
      ))}
    </Muted>
  );
};

// The one-line empty state inside an accordion ("No open conditions").
const DoneLine = ({ text }) => (
  <Flx ac g={1.125} sx={{ color: status.success.fg, py: "7px", px: "2px" }}>
    <CheckCircleOutlineRounded sx={{ fontSize: 15 }} />
    <Txt sx={{ ...TYPE.row, color: status.success.fg }}>{text}</Txt>
  </Flx>
);

// ── 2. Scheduling entries (the "valid signal" contract) ─────────────────────
// `scheduleEdit` = { canEdit, onSaved(res), onGoTo(field) } from the host. With
// it, every entry the schedule PUT can write becomes clickable: a menu offers
// to edit it right here (a one-field modal, ScheduleFieldDialog) or to jump
// to the place on the loan file that owns it.
export const SchedulingChecklist = ({ readiness, scheduleEdit }) => {
  const [menu, setMenu] = useState(null); // { anchor, field }
  const [editingField, setEditingField] = useState(null);
  const s = readiness?.scheduling;
  const fields = Array.isArray(s?.fields) ? s.fields : [];
  const writable = useMemo(() => scheduleWritableFields(readiness), [readiness]);
  if (!fields.length) return null;
  const canEdit = !!scheduleEdit?.canEdit;
  const canGoTo = typeof scheduleEdit?.onGoTo === "function";
  const menuDest = menu ? scheduleFieldDestination(menu.field, readiness) : null;
  const missing = new Set(s?.missingRequired || []);
  const dateStr = (v) =>
    v
      ? /T\d{2}:\d{2}/.test(String(v)) && !/T00:00/.test(String(v))
        ? formatDateTime(v, "datetime")
        : formatDate(v)
      : null;
  const required = fields.filter((f) => f.required);
  const owners = [...new Set(fields.filter((f) => missing.has(f.field)).map((f) => f.ownerRole).filter(Boolean))];
  return (
    <Collapsible
      id={SECTION_ID.scheduling}
      right={
        missing.size ? (
          <Count n={`${missing.size} missing`} tone="bad" />
        ) : (
          <Count n="complete" tone="good" />
        )
      }
      title="Scheduling entries"
    >
      <SummaryLine
        parts={[
          missing.size
            ? `${required.length - missing.size}/${required.length} required entered`
            : `All ${required.length} required entries recorded`,
          owners.length ? `waiting on ${owners.join(", ")}` : null,
          s?.cutoffDate ? `cutoff ${formatDate(s.cutoffDate)}${s.cutoffExceeded ? " (exceeded)" : ""}` : null,
          s?.wireBlockName ? `wire-blocked: ${s.wireBlockName}` : null,
        ]}
      />
      {fields.map((f) => {
        const isMissing = missing.has(f.field);
        const val =
          f.value == null || f.value === ""
            ? null
            : typeof f.value === "boolean"
              ? f.value
                ? "Yes"
                : "No"
              : /date/i.test(f.field)
                ? dateStr(f.value)
                : String(f.value);
        const editable = canEdit && writable.has(f.field);
        const isEditing = editingField === f.field;
        return (
          <Box key={f.field}>
            <ChecklistRow
              tone={f.present ? "done" : isMissing ? "missing" : "muted"}
              title={f.label || f.field}
              onClick={
                editable ? (e) => setMenu({ anchor: e.currentTarget, field: f.field }) : undefined
              }
              active={menu?.field === f.field || isEditing}
              actionLabel={`Edit ${f.label || f.field}`}
              right={
                val ? (
                  <Txt
                    sx={{
                      ...(/date|amount|number/i.test(f.field) ? { ...TYPE.mono, color: INK } : { ...TYPE.meta, color: SLATE }),
                      whiteSpace: "nowrap",
                    }}
                  >
                    {val}
                  </Txt>
                ) : null
              }
              pills={[
                !f.required ? { label: "optional", c: slate[400], bg: slate[100] } : null,
                f.ownerRole && !f.present
                  ? { label: f.ownerRole, c: steel.main, bg: steel.tint, title: `Entered by: ${f.ownerRole}` }
                  : null,
              ]}
            />
          </Box>
        );
      })}
      <ScheduleFieldDialog
        open={!!editingField}
        readiness={readiness}
        field={editingField}
        label={fields.find((f) => f.field === editingField)?.label}
        onClose={() => setEditingField(null)}
        onSaved={(res) => {
          setEditingField(null);
          scheduleEdit?.onSaved?.(res);
        }}
      />
      <Menu
        anchorEl={menu?.anchor || null}
        open={!!menu}
        onClose={() => setMenu(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { minWidth: 240 } } }}
      >
        <MenuItem
          dense
          onClick={() => {
            setEditingField(menu.field);
            setMenu(null);
          }}
        >
          <ListItemIcon>
            <EditOutlined sx={{ fontSize: 17 }} />
          </ListItemIcon>
          <ListItemText primary="Edit here" primaryTypographyProps={{ fontSize: 13 }} />
        </MenuItem>
        {canGoTo && menuDest && (
          <MenuItem
            dense
            onClick={() => {
              const field = menu.field;
              setMenu(null);
              scheduleEdit.onGoTo(field);
            }}
          >
            <ListItemIcon>
              <ArrowForwardRounded sx={{ fontSize: 17 }} />
            </ListItemIcon>
            <ListItemText
              primary={`Go to ${menuDest.label}`}
              primaryTypographyProps={{ fontSize: 13 }}
            />
          </MenuItem>
        )}
      </Menu>
    </Collapsible>
  );
};

// ── 3. Gating items (external vs internal) ──────────────────────────────────
const SOURCE_PILL = {
  document: { label: "Docs", c: steel.main, bg: steel.tint },
  condition: { label: "Condition", c: amber.chipText, bg: amber.light },
  exception: { label: "Exception", c: status.error.fg, bg: status.error.bg },
  order: { label: "Order", c: slate[600], bg: slate[100] },
  lock: { label: "Lock", c: status.error.fg, bg: status.error.bg },
  data: { label: "Entry", c: slate[600], bg: slate[100] },
  calendar: { label: "Calendar", c: status.error.fg, bg: status.error.bg },
};

export const GatingItems = ({ readiness }) => {
  const items = Array.isArray(readiness?.gatingItems) ? readiness.gatingItems : [];
  const ext = items.filter((g) => g.kind === "external");
  const int = items.filter((g) => g.kind !== "external");
  const hard = items.filter((g) => g.hard).length;
  const blocking = items.filter((g) => g.blocksFunding && !g.hard).length;
  const info = items.length - hard - blocking;
  return (
    <Collapsible
      nested
      right={
        <>
          <Pill label={`ext ${readiness?.externalGating ?? ext.length}`} c={slate[600]} bg={slate[100]} size="xs" />
          <Pill label={`int ${readiness?.internalGating ?? int.length}`} c={slate[600]} bg={slate[100]} size="xs" />
        </>
      }
      title="Gating funding"
    >
      {!items.length ? (
        <DoneLine text="Nothing gating the wire" />
      ) : (
        <>
          <SummaryLine
            parts={[
              hard ? `${hard} hard stop${hard === 1 ? "" : "s"}` : null,
              `${blocking} block${blocking === 1 ? "s" : ""} funding`,
              info ? `${info} informational` : null,
            ]}
          />
          <GatingGroup kind="external" list={ext} />
          <GatingGroup kind="internal" list={int} />
        </>
      )}
    </Collapsible>
  );
};

// One kind of gating item (external = waiting on someone else, internal = ours
// to clear), foldable on its own and obeying the host's collapse-all.
const GatingGroup = ({ kind, list }) => {
  const km = gatingKindMeta(kind);
  const [open, setOpen] = useCollapsible();
  if (!list.length) return null;
  return (
    // Sub-group: header on R1, tight to its own rows, 6px between consecutive
    // groups; rows step in to R2.
    <Box sx={{ "& + &": { mt: "6px" } }}>
      <Flx
        ac
        g={0.75}
        onClick={() => setOpen((o) => !o)}
        sx={{
          px: "2px",
          py: "5px",
          mb: open ? "2px" : 0,
          borderRadius: "6px",
          cursor: "pointer",
          userSelect: "none",
          "&:hover": { background: slate[100] },
        }}
      >
        <KeyboardArrowDownRounded
          sx={{
            fontSize: 16,
            color: slate[400],
            transition: "transform 0.15s",
            transform: open ? "none" : "rotate(-90deg)",
            flexShrink: 0,
          }}
        />
        <Txt sx={TYPE.group}>{km.label}</Txt>
        <Muted>
          {kind === "external" ? "waiting on someone else" : "ours to clear"} · {list.length}
        </Muted>
      </Flx>
      {open && (
        <Box sx={{ pl: R2_STEP }}>
          {list.map((g, i) => {
            const sp = SOURCE_PILL[g.source] || { label: g.source, c: slate[600], bg: slate[100] };
            return (
              <ChecklistRow
                key={`${g.refId || g.label}-${i}`}
                tone={g.hard ? "hard" : g.blocksFunding ? "open" : "muted"}
                title={g.label}
                right={<RowMeta text={[g.owner, g.ageDays != null ? `${g.ageDays}d` : null].filter(Boolean).join(" · ")} />}
                pills={[sp]}
                detail={g.detail && g.detail !== g.label ? g.detail : null}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
};

// ── 4. Conditions ───────────────────────────────────────────────────────────
const GATE_PILL = {
  "Prior to Docs": { label: "Docs", c: steel.main, bg: steel.tint },
  "Prior to Funding": { label: "Funding", c: amber.chipText, bg: amber.light },
  "Post Close": { label: "Post close", c: slate[600], bg: slate[100] },
};

const conditionDone = (c) => !!c?.resolvedAt || /accept|clear|complete/i.test(c?.status || "");

const ConditionRow = ({ c }) => {
  const done = conditionDone(c);
  const gate = gateLabel(c?.gate);
  const unspecified = gate === "Unspecified";
  const title = c?.descriptionShort || c?.requirementType || c?.docType || "Condition";
  const detail = c?.description && c.description !== c.descriptionShort ? c.description : null;
  const activity = [
    c?.uploadsSince > 0 ? `${c.uploadsSince} upload${c.uploadsSince === 1 ? "" : "s"} since — re-review` : null,
    c?.commentsSince > 0 ? `${c.commentsSince} comment${c.commentsSince === 1 ? "" : "s"}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const sub = done
    ? [c?.resolvedAt ? `cleared ${formatDate(c.resolvedAt)}` : null, c?.resolvedBy].filter(Boolean).join(" by ")
    : activity || null;
  return (
    <ChecklistRow
      tone={done ? "done" : "open"}
      strike={done}
      title={title}
      right={<RowMeta text={sub} />}
      detail={detail}
      pills={[
        !done && !unspecified ? GATE_PILL[gate] : null,
        !done && unspecified
          ? { label: "No gate", c: amber.chipText, bg: amber.light, title: "Treated as Prior to Funding — ask the UW to set it." }
          : null,
        !done && c?.kind === "external" ? { label: "External", c: steel.main, bg: steel.tint } : null,
      ]}
    />
  );
};

export const ConditionsBlock = ({ readiness }) => {
  const c = readiness?.conditions || {};
  const open = Array.isArray(c.openItems) ? c.openItems : [];
  const cleared = Array.isArray(c.clearedItems) ? c.clearedItems : [];
  const [showCleared, setShowCleared] = useState(false);
  const ext = open.filter((x) => x.kind === "external").length;
  const int = open.length - ext;
  const ages = open.map((x) => x.ageDays).filter((n) => n != null);
  const oldest = ages.length ? Math.max(...ages) : null;
  const ungated = c.openUngated ?? open.filter((x) => gateLabel(x.gate) === "Unspecified").length;
  return (
    <Collapsible
      right={
        <>
          {c.openPriorToFunding > 0 && (
            <Pill label={`${c.openPriorToFunding} prior to funding`} c={amber.chipText} bg={amber.light} size="xs" />
          )}
          <Metric
            n={`${c.cleared ?? cleared.length}/${c.total ?? open.length + cleared.length}`}
            label="cleared"
          />
        </>
      }
      title="Conditions"
    >
      {!open.length ? (
        <DoneLine text={cleared.length ? "No open conditions" : "No conditions issued yet"} />
      ) : (
        <>
          <SummaryLine
            parts={[
              ext && int ? `${ext} external · ${int} internal` : ext ? `All ${ext} external` : `All ${int} internal`,
              oldest != null ? `oldest open ${oldest} day${oldest === 1 ? "" : "s"}` : null,
              ungated ? `${ungated} ${ungated === 1 ? "has" : "have"} no gate set` : null,
            ]}
          />
          {open.map((item, i) => (
            <ConditionRow key={item?.id || i} c={item} />
          ))}
        </>
      )}
      {cleared.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Flx
            ac
            g={1}
            onClick={() => setShowCleared((v) => !v)}
            sx={{ cursor: "pointer", width: "fit-content", userSelect: "none" }}
          >
            <KeyboardArrowDownRounded
              sx={{
                fontSize: 16,
                color: slate[400],
                transition: "transform 0.15s",
                transform: showCleared ? "rotate(180deg)" : "none",
              }}
            />
            <Txt sx={TYPE.toggle}>{showCleared ? "Hide" : "Show"} {cleared.length} cleared</Txt>
          </Flx>
          {showCleared && (
            <Box sx={{ mt: 0.5 }}>
              {cleared.map((item, i) => (
                <ConditionRow key={item?.id || i} c={item} />
              ))}
            </Box>
          )}
        </Box>
      )}
    </Collapsible>
  );
};

// ── 5. Exceptions ───────────────────────────────────────────────────────────
// One exception as a checklist row: open → amber ring (red warning when
// hard); resolved → done row with "Approved · Sep 11 · by …" on the right.
// The full text expands in place when it was truncated.
const ExceptionRow = ({ ex }) => {
  const done = !!ex?.resolvedAt || /approved|resolved|rejected|closed/i.test(ex?.status || "");
  const title = ex?.descriptionShort || ex?.category || "Exception";
  const detail = ex?.description && ex.description !== ex.descriptionShort ? ex.description : null;
  const meta = done
    ? [ex?.status, ex?.resolvedAt ? formatDate(ex.resolvedAt) : null, ex?.resolvedBy ? `by ${ex.resolvedBy}` : null]
        .filter(Boolean)
        .join(" · ")
    : [ex?.assignedTo, ex?.ageDays != null ? `${ex.ageDays}d open` : null].filter(Boolean).join(" · ");
  return (
    <ChecklistRow
      tone={done ? "done" : ex?.isHardException ? "hard" : "open"}
      title={title}
      right={<RowMeta text={meta} />}
      detail={detail}
      pills={[
        ex?.category ? { label: ex.category, c: slate[600], bg: slate[100] } : null,
        !done && ex?.isHardException ? { label: "Hard", c: status.error.fg, bg: status.error.bg } : null,
        done && /rejected/i.test(ex?.status || "") ? { label: "Rejected", c: status.error.fg, bg: status.error.bg } : null,
      ]}
    />
  );
};

export const ExceptionsBlock = ({ readiness }) => {
  const e = readiness?.exceptions || {};
  const open = Array.isArray(e.openItems) ? e.openItems : [];
  const closed = Array.isArray(e.closedItems) ? e.closedItems : [];
  const [showClosed, setShowClosed] = useState(false);
  if (!open.length && !closed.length) return null;
  const assignees = [...new Set(open.map((x) => x.assignedTo).filter(Boolean))];
  return (
    <Collapsible
      right={
        <>
          {e.openHard > 0 && <Pill label={`${e.openHard} hard`} c={status.error.fg} bg={status.error.bg} size="xs" />}
          <Count n={`${e.open ?? open.length} open`} tone={e.open ? "bad" : "neutral"} />
          {(e.approved ?? 0) > 0 && <Count n={`${e.approved} approved`} tone="good" />}
          {(e.rejected ?? 0) > 0 && <Count n={`${e.rejected} rejected`} tone="bad" />}
        </>
      }
      title="Exceptions"
    >
      {!open.length ? (
        <DoneLine
          text={
            e.approved
              ? `${e.approved} exception${e.approved === 1 ? "" : "s"} approved; none outstanding`
              : "No open exceptions"
          }
        />
      ) : (
        <>
          <SummaryLine
            parts={[
              `${open.length} awaiting decision`,
              e.openHard ? `${e.openHard} hard` : null,
              assignees.length ? `with ${assignees.join(", ")}` : null,
            ]}
          />
          {open.map((ex, i) => (
            <ExceptionRow key={ex?.id || i} ex={ex} />
          ))}
        </>
      )}
      {closed.length > 0 && (
        <Box sx={{ mt: open.length ? 1 : 0 }}>
          <Flx
            ac
            g={1}
            onClick={() => setShowClosed((v) => !v)}
            sx={{ cursor: "pointer", width: "fit-content", userSelect: "none" }}
          >
            <KeyboardArrowDownRounded
              sx={{
                fontSize: 16,
                color: slate[400],
                transition: "transform 0.15s",
                transform: showClosed ? "rotate(180deg)" : "none",
              }}
            />
            <Txt sx={TYPE.toggle}>
              {showClosed ? "Hide" : "Show"} {closed.length} resolved
            </Txt>
          </Flx>
          {showClosed && (
            <Box sx={{ mt: 0.5 }}>
              {closed.map((ex, i) => (
                <ExceptionRow key={ex?.id || i} ex={ex} />
              ))}
            </Box>
          )}
        </Box>
      )}
    </Collapsible>
  );
};

// ── 6. Documents (per-object cards: what each party still owes) ─────────────
// One card per object the backend tracks presence for (`missingDocuments[]`:
// loan / borrower / subjectProperty / entity), each with a "missing N" count and
// a completion ring; inside, the present gates as file rows (clickable →
// preview when the loan doc resolves) and the missing gates as dashed
// placeholders. Filter chips narrow every card to All / Missing / In review /
// Accepted. Files that don't map to a gate land in a trailing "Other uploads".

const DOC_FILTERS = [
  { key: "all", label: "All" },
  { key: "missing", label: "Missing" },
  { key: "review", label: "In review" },
  { key: "accepted", label: "Accepted" },
];

const docStatusKind = (s) => {
  const v = String(s || "").toLowerCase();
  if (/accept/.test(v)) return "accepted";
  if (/reject|fail/.test(v)) return "rejected";
  return "review"; // new upload / pending / processor reviewed / anything else
};

const OBJECT_META = {
  borrower: { label: "Borrower", icon: null },
  subjectProperty: { label: "Subject property", icon: HomeWorkOutlined },
  entity: { label: "Borrowing entity", icon: BusinessOutlined },
  loan: { label: "Loan file", icon: DescriptionOutlined },
};

const initials = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "•";

// Loose gate ↔ file match: a presence gate ("Loan Application") vs a file's
// docType ("Loan Application", "Loan application - signed", …).
const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
const fileMatchesGate = (file, gate) => {
  const g = norm(gate);
  const t = norm(file?.docType);
  if (!g || !t) return false;
  return t === g || t.includes(g) || g.includes(t);
};

// Small completion ring (present / total).
const Ring = ({ done, total, size = 30 }) => {
  const pct = total ? Math.min(1, done / total) : 0;
  const r = (size - 4) / 2;
  const c = 2 * Math.PI * r;
  const tone = pct >= 1 ? status.success.solid : pct > 0 ? amber.main : slate[300];
  return (
    <Box sx={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={slate[200]} strokeWidth={2.5} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={tone}
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct)}
        />
      </svg>
      <Txt
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: MONO,
          fontSize: 9.5,
          fontWeight: 700,
          color: slate[600],
        }}
      >
        {done}/{total}
      </Txt>
    </Box>
  );
};

const FileRow = ({ row, resolveDoc, onPreviewDoc, title }) => {
  const full = typeof resolveDoc === "function" ? resolveDoc(row?.id, row) : null;
  const clickable = !!full && typeof onPreviewDoc === "function";
  const kind = docStatusKind(row?.status);
  const sm = docStatusMeta(row?.status);
  return (
    <Flx
      ac
      g={1.25}
      onClick={clickable ? () => onPreviewDoc(full) : undefined}
      sx={{
        px: 1.25,
        py: 1,
        borderRadius: "8px",
        border: `1px solid ${kind === "review" ? amber.light : slate[200]}`,
        background: "#fff",
        cursor: clickable ? "pointer" : "default",
        "&:hover": clickable ? { borderColor: steel.main } : {},
        "&:hover .doc-name": clickable ? { color: steel.main } : {},
      }}
    >
      <Flx center sx={{ width: 26, height: 26, borderRadius: "7px", background: slate[100], flexShrink: 0 }}>
        <DescriptionOutlined sx={{ fontSize: 15, color: slate[500] }} />
      </Flx>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Txt className="doc-name" sx={{ ...TYPE.row, display: "block" }}>
          {title || row?.docType || "Document"}
        </Txt>
        <Txt
          sx={{
            ...TYPE.mono,
            fontSize: 11,
            color: MUTED_INK,
            display: "block",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {row?.fileName || row?.docType}
        </Txt>
      </Box>
      {row?.status && (
        <Pill label={kind === "review" ? "In review" : row.status} c={sm.c} bg={sm.bg} size="xs" />
      )}
    </Flx>
  );
};

// A missing slot. Click opens the upload modal that fills it (`onUpload`).
const MissingRow = ({ gate, onUpload }) => (
  <Flx
    ac
    g={1.25}
    onClick={onUpload ? () => onUpload(gate) : undefined}
    sx={{
      px: 1.25,
      py: 1,
      borderRadius: "8px",
      border: `1px dashed ${slate[300]}`,
      background: slate[50],
      cursor: onUpload ? "pointer" : "default",
      transition: "border-color 0.12s, background 0.12s",
      "&:hover": onUpload ? { borderColor: steel.main, background: "#fff" } : {},
      "&:hover .slot-add": onUpload ? { borderColor: steel.main, color: steel.main } : {},
    }}
  >
    <Flx
      center
      className="slot-add"
      sx={{ width: 26, height: 26, borderRadius: "7px", border: `1px solid ${slate[200]}`, color: slate[400], flexShrink: 0 }}
    >
      <AddRounded sx={{ fontSize: 15 }} />
    </Flx>
    <Txt sx={{ ...TYPE.row, color: SLATE, flex: 1 }}>{gate}</Txt>
    {onUpload && <Muted sx={{ fontSize: 11 }}>Upload</Muted>}
  </Flx>
);

// The object card's frame — header (chevron · avatar · title + subtitle ·
// right slot) over a foldable body. Shared by the Documents card and the
// Document expirations card so the two stay one design.
const ObjectCardShell = ({ avatar, title, subtitle, right, defaultOpen, children }) => {
  const [open, setOpen] = useCollapsible(defaultOpen);
  return (
    <Box sx={{ border: `1px solid ${slate[200]}`, borderRadius: "12px", overflow: "hidden", background: "#fff" }}>
      <Flx
        ac
        g={1.25}
        onClick={() => setOpen((o) => !o)}
        sx={{ px: 1.5, py: 1.25, cursor: "pointer", userSelect: "none", "&:hover": { background: slate[50] } }}
      >
        <KeyboardArrowDownRounded
          sx={{
            fontSize: 18,
            color: slate[400],
            transition: "transform 0.15s",
            transform: open ? "none" : "rotate(-90deg)",
            flexShrink: 0,
          }}
        />
        {avatar}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Txt
            sx={{
              ...TYPE.row,
              fontWeight: 700,
              display: "block",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {title}
          </Txt>
          <Muted sx={{ display: "block" }}>{subtitle}</Muted>
        </Box>
        {right}
      </Flx>
      {open && <Box sx={{ px: 1.5, pb: 1.5, borderTop: `1px solid ${slate[100]}` }}>{children}</Box>}
    </Box>
  );
};

// The 32px object avatar: a glyph for property / entity / loan, initials for
// a person (steel-tinted, like the People list).
const ObjectAvatar = ({ objectType, label }) => {
  const Icon = (OBJECT_META[objectType] || OBJECT_META.loan).icon;
  const person = objectType === "borrower";
  return (
    <Flx
      center
      sx={{
        width: 32,
        height: 32,
        borderRadius: "8px",
        background: person ? steel.tint : slate[100],
        color: person ? steel.main : slate[600],
        fontSize: 11.5,
        fontWeight: 800,
        flexShrink: 0,
      }}
    >
      {Icon ? <Icon sx={{ fontSize: 17 }} /> : initials(label)}
    </Flx>
  );
};

// A card for one object. `extraFiles` (the "Other uploads" card) lists files
// that no gate claimed; those render as plain file rows.
const ObjectCard = ({ obj, files, extraFiles, filter, resolveDoc, onPreviewDoc, defaultOpen, subtitle, onUploadMissing }) => {
  const meta = OBJECT_META[obj.objectType] || OBJECT_META.loan;
  const missing = Array.isArray(obj.missing) ? obj.missing : [];
  const present = Array.isArray(obj.present) ? obj.present : [];
  const total = missing.length + present.length;
  const extras = Array.isArray(extraFiles) ? extraFiles : [];

  // Present gates → their file rows (may be several files per gate).
  const presentRows = present.map((gate) => ({
    gate,
    files: (files || []).filter((f) => fileMatchesGate(f, gate)),
  }));
  const visiblePresent = presentRows.filter(
    (r) =>
      filter === "all" ||
      (filter !== "missing" && r.files.some((f) => docStatusKind(f.status) === filter)),
  );
  const visibleMissing = filter === "all" || filter === "missing" ? missing : [];
  // Satisfied gates with no pairable file are not listed (the ring still
  // counts them — the backend says the requirement is met).
  if (
    filter !== "all" &&
    !visiblePresent.some((r) => r.files.length) &&
    !visibleMissing.length &&
    !extras.length
  ) {
    return null;
  }

  return (
    <ObjectCardShell
      defaultOpen={defaultOpen}
      avatar={<ObjectAvatar objectType={obj.objectType} label={obj.objectLabel} />}
      title={obj.objectType === "loan" && obj.objectLabel === "loan" ? "Loan file" : obj.objectLabel}
      subtitle={subtitle || meta.label}
      right={
        <>
          {missing.length > 0 && (
            <Txt sx={{ ...TYPE.metaStrong, color: status.error.fg, whiteSpace: "nowrap" }}>
              missing {missing.length}
            </Txt>
          )}
          {total > 0 && <Ring done={present.length} total={total} />}
        </>
      }
    >
      <Muted sx={{ display: "block", py: 1 }}>
        {missing.length
          ? `${missing.length} required document${missing.length === 1 ? "" : "s"} outstanding`
          : extras.length
            ? `${extras.length} file${extras.length === 1 ? "" : "s"}`
            : "Nothing outstanding"}
      </Muted>
      <Flx column g={0.75}>
        {visiblePresent
          .filter((r) => r.files.length)
          .map((r) =>
            r.files.map((f, i) => (
              <FileRow
                key={`${r.gate}-${f.id || i}`}
                row={f}
                title={r.gate}
                resolveDoc={resolveDoc}
                onPreviewDoc={onPreviewDoc}
              />
            )),
          )}
        {visibleMissing.map((gate) => (
          <MissingRow key={gate} gate={gate} onUpload={onUploadMissing ? (gt) => onUploadMissing(obj, gt) : undefined} />
        ))}
        {extras.map((f, i) => (
          <FileRow key={f.id || i} row={f} resolveDoc={resolveDoc} onPreviewDoc={onPreviewDoc} />
        ))}
      </Flx>
    </ObjectCardShell>
  );
};

// The filter-chip strip on top of a card body: one chip per filter, the
// active one filled ink, each (but "All") carrying its count.
const FilterChips = ({ filters, value, onChange, counts }) => (
  <Flx ac wrap g={0.75} sx={{ mb: 1.5 }}>
    {filters.map((f) => {
      const active = value === f.key;
      return (
        <Flx
          ac
          g={0.6}
          key={f.key}
          onClick={() => onChange(f.key)}
          sx={{
            px: 1.4,
            py: 0.5,
            borderRadius: "99px",
            ...TYPE.micro,
            fontSize: 12,
            cursor: "pointer",
            userSelect: "none",
            background: active ? slate[900] : "#fff",
            color: active ? "#fff" : slate[600],
            border: `1px solid ${active ? slate[900] : slate[200]}`,
            "&:hover": { borderColor: active ? slate[900] : slate[400] },
          }}
        >
          {f.label}
          {f.key !== "all" && (
            <Box
              component="span"
              sx={{ ...TYPE.microMono, color: active ? "rgba(255,255,255,.7)" : slate[400] }}
            >
              {counts[f.key]}
            </Box>
          )}
        </Flx>
      );
    })}
  </Flx>
);

// The completion bar beside a card's metric.
const MetricBar = ({ pct }) => (
  <LinearProgress
    variant="determinate"
    value={Math.max(0, Math.min(100, pct))}
    sx={{
      width: 56,
      height: 5,
      borderRadius: 99,
      background: slate[100],
      "& .MuiLinearProgress-bar": {
        borderRadius: 99,
        background: pct >= 100 ? status.success.solid : amber.main,
      },
    }}
  />
);

export const DocumentsBlock = ({ readiness, resolveDoc, onPreviewDoc, loan, onDocsChanged }) => {
  const d = readiness?.documents || {};
  // The upload modal for a missing slot: { obj, gate } while open.
  const [uploadSlot, setUploadSlot] = useState(null);
  const openUpload = (obj, gate) => setUploadSlot({ obj, gate });
  const items = Array.isArray(d.items) ? d.items : [];
  const objects = Array.isArray(readiness?.missingDocuments) ? readiness.missingDocuments : [];
  const [filter, setFilter] = useState("all");
  const pct = Number(d.acceptedPct) || 0;

  const missingCount = objects.reduce((n, o) => n + (o.missing?.length || 0), 0);
  const counts = {
    all: items.length + missingCount,
    missing: missingCount,
    review: items.filter((f) => docStatusKind(f.status) === "review").length,
    accepted: items.filter((f) => docStatusKind(f.status) === "accepted").length,
  };

  // Files no gate claims → "Other uploads".
  const claimed = new Set();
  objects.forEach((o) =>
    (o.present || []).forEach((gate) =>
      items.forEach((f) => {
        if (fileMatchesGate(f, gate)) claimed.add(f.id || f.fileName);
      }),
    ),
  );
  const others = items.filter(
    (f) => !claimed.has(f.id || f.fileName) && (filter === "all" || docStatusKind(f.status) === filter),
  );

  // Cards in the reading order: borrowers → properties → entities → loan.
  const order = { borrower: 0, subjectProperty: 1, entity: 2, loan: 3 };
  const cards = [...objects].sort((a, b) => (order[a.objectType] ?? 9) - (order[b.objectType] ?? 9));
  const loanSubtitle = [readiness?.loanNumber != null ? `#${readiness.loanNumber}` : null, readiness?.syncLoanId]
    .filter(Boolean)
    .join(" · ");

  return (
    <Collapsible
      id={SECTION_ID.documents}
      nested
      right={
        <>
          <Metric n={`${d.accepted ?? 0}/${d.totalVisible ?? items.length}`} label="accepted" />
          <MetricBar pct={pct} />
        </>
      }
      title="Documents"
    >
      <FilterChips filters={DOC_FILTERS} value={filter} onChange={setFilter} counts={counts} />

      {!cards.length && !items.length ? (
        <Muted>No documents</Muted>
      ) : (
        <Flx column g={1}>
          {cards.map((obj, i) => (
            <ObjectCard
              key={`${obj.objectType}-${obj.objectLabel}-${i}`}
              obj={obj}
              files={items}
              filter={filter}
              resolveDoc={resolveDoc}
              onPreviewDoc={onPreviewDoc}
              onUploadMissing={readiness?.dealId ? openUpload : undefined}
              defaultOpen={i === 0}
              subtitle={obj.objectType === "loan" ? loanSubtitle || undefined : undefined}
            />
          ))}
          {others.length > 0 && (
            <ObjectCard
              obj={{ objectType: "loan", objectLabel: "Other uploads", missing: [], present: [] }}
              files={[]}
              extraFiles={others}
              filter={filter}
              resolveDoc={resolveDoc}
              onPreviewDoc={onPreviewDoc}
              defaultOpen={false}
              subtitle={`${others.length} file${others.length === 1 ? "" : "s"} not tied to a requirement`}
            />
          )}
        </Flx>
      )}
      <MissingDocUploadModal
        show={Boolean(uploadSlot)}
        onClose={() => setUploadSlot(null)}
        dealId={readiness?.dealId}
        gate={uploadSlot?.gate}
        obj={uploadSlot?.obj}
        loan={loan}
        onUploaded={onDocsChanged}
      />
    </Collapsible>
  );
};

// ── 6b. Document expirations ────────────────────────────────────────────────
// Every dated document, sorted into the three answers a closer needs:
//   Expired - Needs replacing   expired now, or lapses before closing
//   Expiring soon               inside the 10-day window (today or at closing)
//   Valid                       good through closing (starts folded)
// Built exactly like the Documents card — gray body, filter chips, then one
// object-style card per answer — and each row is the Documents FileRow with a
// colored left edge; hovering a row that needs attention offers Upload.
//
// The reference days come from docValidityModel, i.e. the shared rules (see
// "Document validity & expiration" in CLAUDE.md): a live loan is checked on
// today AND its upcoming closing; a closed loan is frozen on its anchor date.
// The one-line anchor strip says which, and its (i) spells out the rule.

const EXP_TONE = {
  expired: { c: status.error.solid, bg: status.error.bg },
  lapse: { c: status.warning.solid, bg: status.warning.bg },
  soon: { c: amber.main, bg: amber.light },
  valid: { c: status.success.solid, bg: status.success.bg },
};
const EXP_RANK = { expired: 0, lapse: 1, soon: 2, valid: 3 };
const md = (ms) => formatDate(dayKey(ms), "—", "MMM d");
const wmd = (ms) => formatDate(dayKey(ms), "—", "EEE, MMM d");

// Which object a document belongs to (first match: borrower › property ›
// entity), else the loan file. `label` is the object's full label (what the
// upload modal resolves associations by); `short` is what the row shows.
const docOwners = (loan) => {
  const byId = new Map();
  const add = (objectType, label, short, ids) =>
    (ids || []).forEach((id) => {
      if (!byId.has(id)) byId.set(id, { objectType, label, short });
    });
  (loan?.borrowers || []).forEach((b) => {
    const label = [b?.firstName, b?.lastName].filter(Boolean).join(" ") || "Borrower";
    const parts = label.split(/\s+/);
    add("borrower", label, parts.length > 2 ? `${parts[0]} ${parts[parts.length - 1]}` : label, b?.associations?.loanDocuments);
  });
  (loan?.subjectProperties || []).forEach((p) => {
    const label = addressLabel(p?.address) || "Subject property";
    add("subjectProperty", label, label.replace(/,\s*\d{5}(-\d{4})?$/, ""), p?.associations?.loanDocuments);
  });
  (Array.isArray(loan?.entities) ? loan.entities : loan?.borrowerEntity ? [loan.borrowerEntity] : []).forEach((e) => {
    const label = e?.entityName || e?.name || "Entity";
    add("entity", label, label, e?.associations?.loanDocuments);
  });
  const loanFile = { objectType: "loan", label: "loan", short: "Loan file" };
  return (id) => byId.get(id) || loanFile;
};

// One row's category and its right-hand answer, from the model's per-day
// statuses. `anchor` is the upcoming closing on a live loan, the frozen close
// on a closed one.
const classifyExpRow = (row, model) => {
  const hasAnchor = model.refs.some((r) => r.key === "anchor");
  const exp = md(row.expMs);
  if (model.closed && hasAnchor) {
    const n = row.daysLeft.anchor;
    const st = row.at.anchor;
    if (st === "expired") return { cat: "expired", when: `Expired ${-n}d before close`, sub: `Exp. ${exp}` };
    if (st === "expiring") return { cat: "soon", when: `${n}d left at close`, sub: `Exp. ${exp}` };
    return { cat: "valid", when: `Valid to ${exp}`, sub: `${n}d past close` };
  }
  const t = row.daysLeft.today;
  if (row.at.today === "expired")
    return { cat: "expired", when: t === -1 ? "Expired yesterday" : `Expired ${-t}d ago`, sub: `Exp. ${exp}` };
  if (!hasAnchor) {
    if (row.at.today === "expiring")
      return { cat: "soon", when: t === 0 ? "Expires today" : `Expires in ${t}d`, sub: `Exp. ${exp}` };
    return { cat: "valid", when: `Valid to ${exp}`, sub: `${t}d left` };
  }
  const c = row.daysLeft.anchor;
  if (row.at.anchor === "expired") return { cat: "lapse", when: `Expires ${exp}`, sub: `${-c}d before closing` };
  if (row.at.today === "expiring" || row.at.anchor === "expiring")
    return { cat: "soon", when: c === 0 ? "Expires closing day" : `${c}d past closing`, sub: `Exp. ${exp}` };
  return { cat: "valid", when: `Valid to ${exp}`, sub: `${c}d past closing` };
};

// "● 5 expired" — the header's count tags.
const DotTag = ({ tone, children }) => (
  <Flx
    ac
    sx={{ gap: "5px", fontSize: 12, fontWeight: 500, px: 1, py: "2px", borderRadius: "999px", background: tone.bg, color: tone.c, whiteSpace: "nowrap" }}
  >
    <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: tone.c }} />
    {children}
  </Flx>
);

// A dated file: the Documents card's FileRow — same frame, icon tile, title
// and mono filename — plus a colored left edge for its category. The right
// column carries the answer (a pill + the date line); hovering a row that
// needs attention swaps it for "Upload new copy".
const ExpFileRow = ({ item, onPreviewDoc, onUpload }) => {
  const tone = EXP_TONE[item.cat];
  const doc = item.row.doc;
  const clickable = typeof onPreviewDoc === "function" && !!doc;
  const canUpload = typeof onUpload === "function" && item.cat !== "valid";
  return (
    <Flx
      ac
      g={1.25}
      onClick={clickable ? () => onPreviewDoc(doc) : undefined}
      sx={{
        px: 1.25,
        py: 1,
        borderRadius: "8px",
        border: `1px solid ${slate[200]}`,
        borderLeft: `3px solid ${tone.c}`,
        background: "#fff",
        cursor: clickable ? "pointer" : "default",
        "&:hover": clickable ? { borderColor: steel.main, borderLeftColor: tone.c } : {},
        "&:hover .doc-name": clickable ? { color: steel.main } : {},
        ...(canUpload
          ? {
              "& .exp-acts": { display: "none" },
              "&:hover .exp-acts, &:focus-within .exp-acts": { display: "flex" },
              "&:hover .exp-answer, &:focus-within .exp-answer": { display: "none" },
              "@media (hover: none)": { "& .exp-acts": { display: "flex" }, "& .exp-answer": { display: "none" } },
            }
          : null),
      }}
    >
      <Flx center sx={{ width: 26, height: 26, borderRadius: "7px", background: slate[100], flexShrink: 0 }}>
        <DescriptionOutlined sx={{ fontSize: 15, color: slate[500] }} />
      </Flx>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Flx sx={{ alignItems: "baseline", gap: 0.75, minWidth: 0 }}>
          <Txt className="doc-name" sx={{ ...TYPE.row, whiteSpace: "nowrap" }}>
            {doc?.docType || "Document"}
          </Txt>
          <Txt sx={{ ...TYPE.meta, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {item.owner.short}
          </Txt>
        </Flx>
        <Txt
          title={doc?.file_display_name}
          sx={{
            ...TYPE.mono,
            fontSize: 11,
            color: MUTED_INK,
            display: "block",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {doc?.file_display_name || doc?.docType}
        </Txt>
      </Box>
      <Flx column className="exp-answer" sx={{ alignItems: "flex-end", gap: "3px", flexShrink: 0 }}>
        <Pill label={item.when} c={tone.c} bg={tone.bg} size="xs" />
        <Txt sx={{ ...TYPE.mono, fontSize: 11, color: MUTED_INK, whiteSpace: "nowrap" }}>{item.sub}</Txt>
      </Flx>
      {canUpload && (
        <Flx className="exp-acts" sx={{ flexShrink: 0 }}>
          <Box
            component="button"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onUpload(item);
            }}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              border: `1px solid ${slate[200]}`,
              background: "#fff",
              borderRadius: "6px",
              px: 1,
              py: "4px",
              fontSize: 12,
              fontWeight: 500,
              fontFamily: "inherit",
              color: slate[600],
              cursor: "pointer",
              "&:hover": { borderColor: slate[400], color: slate[900] },
            }}
          >
            <FileUploadOutlined sx={{ fontSize: 14 }} />
            Upload new copy
          </Box>
        </Flx>
      )}
    </Flx>
  );
};

// The three category cards — the Documents card's object cards, one per
// answer. `glyph` is the avatar icon, tinted in the category's tone.
const EXP_GROUPS = [
  {
    key: "needs",
    icon: ErrorOutline,
    tone: "expired",
    defaultOpen: false,
  },
  {
    key: "soon",
    icon: ScheduleRounded,
    tone: "soon",
    defaultOpen: false,
  },
  {
    key: "valid",
    icon: CheckCircleOutlineRounded,
    tone: "valid",
    defaultOpen: false,
  },
];

const ExpGroupCard = ({ group, title, subtitle, items, empty, onPreviewDoc, onUpload }) => {
  const tone = EXP_TONE[group.tone];
  const Icon = group.icon;
  return (
    <ObjectCardShell
      defaultOpen={group.defaultOpen}
      avatar={
        <Flx center sx={{ width: 32, height: 32, borderRadius: "8px", background: tone.bg, color: tone.c, flexShrink: 0 }}>
          <Icon sx={{ fontSize: 17 }} />
        </Flx>
      }
      title={title}
      subtitle={subtitle}
      right={
        items.length ? (
          <Txt sx={{ ...TYPE.metaStrong, color: group.key === "valid" ? SLATE : tone.c, whiteSpace: "nowrap" }}>
            {items.length} {items.length === 1 ? "doc" : "docs"}
          </Txt>
        ) : (
          <Muted sx={{ whiteSpace: "nowrap" }}>none</Muted>
        )
      }
    >
      <Muted sx={{ display: "block", py: 1 }}>
        {items.length ? `${items.length} document${items.length === 1 ? "" : "s"}` : empty}
      </Muted>
      {items.length > 0 && (
        <Flx column g={0.75}>
          {items.map((it) => (
            <ExpFileRow key={it.row.doc?._id} item={it} onPreviewDoc={onPreviewDoc} onUpload={onUpload} />
          ))}
        </Flx>
      )}
    </ObjectCardShell>
  );
};

// The line naming the day(s) every row is judged on, with the rule in (i).
const ExpAnchorLine = ({ model, trade }) => {
  const strong = { fontWeight: 600, color: INK };
  const anchor = model.refs.find((r) => r.key === "anchor");
  let line;
  let tip;
  if (model.closed && anchor) {
    line = (
      <>
        <Flx ac component="span" sx={{ gap: "4px", color: steel.main, fontWeight: 500 }}>
          <LockRounded sx={{ fontSize: 12 }} />
          Frozen
        </Flx>
        <span>
          Checked against <Box component="span" sx={strong}>{wmd(anchor.dayMs)}</Box>, the {anchor.fieldLabel.toLowerCase()}.
        </span>
      </>
    );
    tip = trade
      ? "Acquisitions are checked against today until Loan Closed? is Yes, then as of the note (origination) date."
      : "Closed pre-close loans are checked as of the note date, then the funded date, then the estimated or expected closing date, whichever is on file first.";
  } else if (model.unresolved) {
    line = (
      <span>
        Checked against <Box component="span" sx={strong}>today, {wmd(model.today)}</Box>
        <Box component="span" sx={{ color: status.warning.fg }}> · loan is closed but no {model.missingFields.join(" / ").toLowerCase()} is on file</Box>
      </span>
    );
    tip = "Once one of those dates is on file, the check freezes on it instead of counting from today.";
  } else if (anchor) {
    line = (
      <span>
        Checked against <Box component="span" sx={strong}>today, {wmd(model.today)}</Box> and{" "}
        <Box component="span" sx={strong}>closing, {wmd(anchor.dayMs)}</Box>
      </span>
    );
    tip = `Open loans are checked twice: today, and the ${anchor.fieldLabel.toLowerCase()} (${
      trade ? "expectedClosingDate" : "closingScheduledDate, else expectedClosingDate"
    }). Once the loan is ${trade ? "marked Loan Closed? = Yes" : "Closed"}, the check freezes on the note date.`;
  } else {
    line = (
      <span>
        Checked against <Box component="span" sx={strong}>today, {wmd(model.today)}</Box>
        <Box component="span" sx={{ color: MUTED_INK }}>
          {model.slipped?.length ? ` · the ${model.slipped.map((s) => s.label.toLowerCase()).join(" and ")} has passed` : " · no closing date on file"}
        </Box>
      </span>
    );
    tip = "With an upcoming closing date on file, every document is also checked against it.";
  }
  return (
    <Flx ac sx={{ flexWrap: "wrap", gap: "4px 8px", mb: 1.25, fontSize: 12.5, color: SLATE }}>
      {line}
      <Tooltip arrow title={tip}>
        <InfoOutlined sx={{ fontSize: 15, color: MUTED_INK, cursor: "help" }} />
      </Tooltip>
    </Flx>
  );
};

export const ExpirationsBlock = ({ loan, trade, onPreviewDoc, onDocsChanged }) => {
  const [uploadFor, setUploadFor] = useState(null);
  const [filter, setFilter] = useState("all");
  const model = buildValidityModel({
    loan,
    documents: (Array.isArray(loan?.loanDocuments) ? loan.loanDocuments : []).filter((d) => !d?.isHidden),
    isAcquisition: !!trade,
  });
  const ownerOf = docOwners(loan);
  const items = model.rows
    .map((row) => ({ row, owner: ownerOf(row.doc?._id), ...classifyExpRow(row, model) }))
    .sort((a, b) => EXP_RANK[a.cat] - EXP_RANK[b.cat] || a.row.expMs - b.row.expMs);
  const expired = items.filter((i) => i.cat === "expired");
  const lapse = items.filter((i) => i.cat === "lapse");
  const byGroup = {
    needs: items.filter((i) => i.cat === "expired" || i.cat === "lapse"),
    soon: items.filter((i) => i.cat === "soon"),
    valid: items.filter((i) => i.cat === "valid"),
  };
  const total = items.length;
  const pct = (n) => `${total ? Math.round((n / total) * 100) : 0}%`;
  const hasAnchor = model.refs.some((r) => r.key === "anchor");
  const frozen = model.closed && hasAnchor;
  const onUpload = loan?._id ? (item) => setUploadFor(item) : undefined;

  const copy = {
    needs: {
      label: "Needs replacing",
      title: frozen ? "Expired at close - Needs replacing" : "Expired - Needs replacing",
      subtitle: frozen ? "Expired on the close date" : hasAnchor ? "Expired, or lapses before closing" : "Expired today",
      empty: frozen ? "Nothing was expired at the close." : hasAnchor ? "Nothing expires before closing." : "Nothing is expired.",
    },
    soon: {
      label: "Expiring soon",
      title: "Expiring soon",
      subtitle: frozen ? "Within 10 days of the close" : hasAnchor ? "Within 10 days of today or closing" : "Within 10 days",
      empty: "Nothing within 10 days.",
    },
    valid: {
      label: "Valid",
      title: "Valid",
      subtitle: frozen ? "Good at the close" : hasAnchor ? "Good through closing" : "Good today",
      empty: "No documents are valid yet.",
    },
  };
  const filters = [{ key: "all", label: "All" }, ...EXP_GROUPS.map((g) => ({ key: g.key, label: copy[g.key].label }))];
  const counts = { all: total, needs: byGroup.needs.length, soon: byGroup.soon.length, valid: byGroup.valid.length };
  const groups = filter === "all" ? EXP_GROUPS : EXP_GROUPS.filter((g) => g.key === filter);

  return (
    <Collapsible
      nested
      // Starts folded on every host (the header already carries the counts and
      // the valid/soon/expired bar), and so does every group card inside it —
      // the reader opens the one band they are chasing.
      defaultOpen={false}
      title="Document expirations"
      right={
        <>
          {expired.length > 0 && <DotTag tone={EXP_TONE.expired}>{expired.length} expired</DotTag>}
          {lapse.length > 0 && <DotTag tone={EXP_TONE.lapse}>{lapse.length} before closing</DotTag>}
          {total > 0 && !byGroup.needs.length && <DotTag tone={EXP_TONE.valid}>All valid</DotTag>}
          <Metric n={`${byGroup.valid.length}/${total}`} label="valid" />
          <Flx sx={{ width: 56, height: 5, borderRadius: 99, background: slate[100], overflow: "hidden" }}>
            <Box sx={{ width: pct(byGroup.valid.length), background: EXP_TONE.valid.c }} />
            <Box sx={{ width: pct(byGroup.soon.length), background: EXP_TONE.soon.c }} />
            <Box sx={{ width: pct(byGroup.needs.length), background: EXP_TONE.expired.c }} />
          </Flx>
        </>
      }
    >
      <ExpAnchorLine model={model} trade={trade} />
      <FilterChips filters={filters} value={filter} onChange={setFilter} counts={counts} />
      {!total ? (
        <Muted>No dated documents on this loan</Muted>
      ) : (
        <Flx column g={1}>
          {groups.map((g) => (
            <ExpGroupCard
              key={g.key}
              group={g}
              title={copy[g.key].title}
              subtitle={copy[g.key].subtitle}
              items={byGroup[g.key]}
              empty={copy[g.key].empty}
              onPreviewDoc={onPreviewDoc}
              onUpload={onUpload}
            />
          ))}
        </Flx>
      )}
      <MissingDocUploadModal
        show={Boolean(uploadFor)}
        onClose={() => setUploadFor(null)}
        dealId={loan?._id}
        gate={uploadFor?.row.doc?.docType}
        obj={uploadFor ? { objectType: uploadFor.owner.objectType, objectLabel: uploadFor.owner.label } : null}
        loan={loan}
        onUploaded={onDocsChanged}
      />
    </Collapsible>
  );
};

// ── 7. Lock + activity + churn ──────────────────────────────────────────────
export const LockBlock = ({ readiness }) => {
  const l = readiness?.lock;
  if (!l?.found) return null;
  const bad = l.expired || l.expiresBeforeFunding;
  return (
    <Collapsible
        right={
          <Pill
            label={l.expired ? "Expired" : l.expiresBeforeFunding ? "Expires before funding" : l.confirmed ? "Confirmed" : l.status || "Lock"}
            c={bad ? status.error.fg : status.success.fg}
            bg={bad ? status.error.bg : status.success.bg}
            size="xs"
          />
        }
      title="Rate lock"
    >
      <Muted>
        {[l.status, l.expirationDate ? `expires ${formatDate(l.expirationDate)}` : null]
          .filter(Boolean)
          .join(" · ")}
      </Muted>
    </Collapsible>
  );
};

const ChurnLine = ({ churn, label }) => {
  const [open, setOpen] = useState(false);
  if (!churn || (!churn.changeCount && !churn.history?.length)) return null;
  return (
    <Box sx={{ mb: 0.75 }}>
      <Flx ac g={0.75}>
        <Txt sx={TYPE.row}>{label}</Txt>
        <Pill
          label={`${churn.changeCount}× changed`}
          c={churn.churn ? amber.chipText : slate[600]}
          bg={churn.churn ? amber.light : slate[100]}
          size="xs"
        />
        {churn.missedDates?.length > 0 && (
          <Pill label={`${churn.missedDates.length} missed`} c={status.error.fg} bg={status.error.bg} size="xs" />
        )}
        {churn.history?.length > 0 && (
          <KeyboardArrowDownRounded
            onClick={() => setOpen((o) => !o)}
            sx={{ fontSize: 16, color: slate[400], cursor: "pointer", transform: open ? "rotate(180deg)" : "none" }}
          />
        )}
      </Flx>
      {churn.lastChangedAt && (
        <Muted sx={{ display: "block" }}>
          last moved {formatDateTime(churn.lastChangedAt, "date")}
          {churn.lastChangedBy ? ` by ${churn.lastChangedBy}` : ""}
          {churn.daysSinceLastChange != null ? ` (${ago(churn.daysSinceLastChange)})` : ""}
        </Muted>
      )}
      {open && (
        <Flx column g={0.25} sx={{ mt: 0.5, pl: 1 }}>
          {churn.history.map((h, i) => (
            <Muted key={i} sx={TYPE.mono}>
              {h.value ? formatDate(h.value) : "—"} · set {formatDateTime(h.at, "datetime")}
              {h.by ? ` by ${h.by}` : ""}
            </Muted>
          ))}
        </Flx>
      )}
    </Box>
  );
};

export const ActivityBlock = ({ readiness }) => {
  const a = readiness?.lastActivity;
  const closer = a?.closer;
  const sinceGate = readiness?.activitySinceGate;
  const hasChurn =
    readiness?.expectedDateChurn?.changeCount || readiness?.firmDateChurn?.changeCount;
  if (!a && !hasChurn) return null;
  return (
    <Collapsible
        right={
          a?.stale ? (
            <Pill label={`Stale · ${a.daysSinceMostRecent ?? "?"}d`} c={amber.chipText} bg={amber.light} size="xs" />
          ) : null
        }
      title="Activity"
    >
      <Flx column g={0.35}>
        {a?.mostRecent && (
          <Muted>
            Last activity {formatDateTime(a.mostRecent, "datetime")}
            {a.mostRecentKind ? ` (${a.mostRecentKind})` : ""}
          </Muted>
        )}
        {closer && (closer.lastAt || closer.closer) && (
          <Muted>
            Closer{closer.closer ? ` ${closer.closer}` : ""}:{" "}
            {closer.lastAt
              ? `last touch ${formatDateTime(closer.lastAt, "datetime")}${closer.lastKind ? ` (${closer.lastKind})` : ""}`
              : "no touches yet"}
            {closer.eventsLast7Days ? ` · ${closer.eventsLast7Days} in 7d` : ""}
            {closer.active ? " · active" : ""}
          </Muted>
        )}
        {sinceGate?.sinceAt && (
          <Muted>
            Since last {sinceGate.sinceKind || "gate"} ({formatDateTime(sinceGate.sinceAt, "date")}):{" "}
            {sinceGate.uploadsSince} upload{sinceGate.uploadsSince === 1 ? "" : "s"},{" "}
            {sinceGate.acceptancesSince} accepted, {sinceGate.commentsSince} comment
            {sinceGate.commentsSince === 1 ? "" : "s"}
            {sinceGate.openConditionsWithActivity
              ? ` · ${sinceGate.openConditionsWithActivity} open condition${sinceGate.openConditionsWithActivity === 1 ? "" : "s"} with new activity`
              : ""}
          </Muted>
        )}
      </Flx>
      {hasChurn ? (
        <Box sx={{ mt: 1 }}>
          <ChurnLine churn={readiness?.expectedDateChurn} label="Expected date" />
          <ChurnLine churn={readiness?.firmDateChurn} label={firmDateLabel(readiness)} />
        </Box>
      ) : null}
    </Collapsible>
  );
};

// ── 8. AI headline (summary of the last closing_readiness run) ───────────────
export const AiReviewBlock = ({ readiness, full, onOpen }) => {
  const s = readiness?.aiReview;
  if (!s && !full) return null;
  const review = full?.review || full;
  return (
    <Collapsible
        right={
          s?.generatedAt ? (
            <Txt sx={{ ...TYPE.mono, color: SLATE, whiteSpace: "nowrap" }}>
              {formatDateTime(s.generatedAt, "datetime")}
              {s.modelUsed ? ` · ${s.modelUsed}` : ""}
            </Txt>
          ) : null
        }
      title="AI readiness call"
    >
      {s?.headline && (
        <Txt sx={{ ...TYPE.body, fontWeight: 600, display: "block" }}>{s.headline}</Txt>
      )}
      <Flx ac g={0.5} sx={{ mt: 0.75 }}>
        {s?.readinessCall && <ReadinessBadge level={s.readinessCall} size="xs" />}
        <Pill label={`ext ${s?.externalGating ?? 0}`} c={steel.main} bg={steel.tint} size="xs" />
        <Pill label={`int ${s?.internalGating ?? 0}`} c={slate[600]} bg={slate[100]} size="xs" />
        {!full && typeof onOpen === "function" && (
          <Txt onClick={onOpen} sx={{ ...TYPE.toggleAccent, cursor: "pointer", ml: 0.5 }}>
            Read the full review
          </Txt>
        )}
      </Flx>
      {full && (
        <Box sx={{ mt: 1.25 }}>
          {/* The AI's own findings render as the SAME checklist rows the
              Conditions / Exceptions / Gating sections use, so the whole
              readiness panel reads as one list vocabulary. */}
          {Array.isArray(review?.gatingItems) && review.gatingItems.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Txt sx={{ ...TYPE.group, display: "block", px: "2px", mb: "2px" }}>Gating items</Txt>
              {review.gatingItems.map((g, i) => {
                const km = gatingKindMeta(g.kind);
                return (
                  <ChecklistRow
                    key={`${g.refId || g.label}-${i}`}
                    tone={g.blocksFunding ? "open" : "muted"}
                    title={g.label}
                    right={
                      <RowMeta
                        text={[
                          g.owner ? `owner: ${g.owner}` : null,
                          g.confidence != null ? `${Math.round(Number(g.confidence) * 100)}%` : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      />
                    }
                    pills={[
                      { label: km.label, c: km.c, bg: km.bg },
                      g.blocksFunding ? { label: "blocks funding", c: status.error.fg, bg: status.error.bg } : null,
                    ]}
                    detail={
                      [g.dependency, g.nextAction ? `Next: ${g.nextAction}` : null]
                        .filter(Boolean)
                        .join("\n") || null
                    }
                  />
                );
              })}
            </Box>
          )}
          {Array.isArray(review?.risks) && review.risks.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Txt sx={{ ...TYPE.group, display: "block", px: "2px", mb: "2px" }}>Risks</Txt>
              {review.risks.map((r, i) => {
                const text = typeof r === "string" ? r : r?.label || r?.text || JSON.stringify(r);
                const detail =
                  typeof r === "object" && r?.text && r?.label && r.text !== r.label ? r.text : null;
                return <ChecklistRow key={i} tone="open" title={text} detail={detail} />;
              })}
            </Box>
          )}
          {Array.isArray(review?.recommendedActions) && review.recommendedActions.length > 0 && (
            <Box>
              <Txt sx={{ ...TYPE.group, display: "block", px: "2px", mb: "2px" }}>Recommended actions</Txt>
              {review.recommendedActions.map((r, i) => {
                const text = typeof r === "string" ? r : r?.label || r?.text || JSON.stringify(r);
                const detail =
                  typeof r === "object" && r?.text && r?.label && r.text !== r.label ? r.text : null;
                return <ChecklistRow key={i} tone="open" title={text} detail={detail} />;
              })}
            </Box>
          )}
        </Box>
      )}
    </Collapsible>
  );
};

// ── Composite ───────────────────────────────────────────────────────────────
// `compact` = the calendar drawer (header + scheduling + gating + conditions +
// documents); the Closing tab renders everything.
const ReadinessDetail = ({
  readiness,
  compact,
  resolveDoc,
  onPreviewDoc,
  aiReviewFull,
  onOpenAiReview,
  // The loan itself (for upload associations) and a refetch hook fired after
  // a missing slot is filled.
  loan,
  onDocsChanged,
  // The host renders <ReadinessHeader> itself (e.g. above the fold control).
  hideHeader = false,
  // { canEdit, onSaved, onGoTo } — makes writable Scheduling entries clickable.
  scheduleEdit,
}) => {
  if (!readiness) return null;
  return (
    <Box>
      {!hideHeader && <ReadinessHeader readiness={readiness} compact={compact} />}
      <BlockGroup>
        <SchedulingChecklist readiness={readiness} scheduleEdit={scheduleEdit} />
        <GatingItems readiness={readiness} />
        <ConditionsBlock readiness={readiness} />
        <ExceptionsBlock readiness={readiness} />
        <DocumentsBlock
          readiness={readiness}
          resolveDoc={resolveDoc}
          onPreviewDoc={onPreviewDoc}
          loan={loan}
          onDocsChanged={onDocsChanged}
        />
        {loan && (
          <ExpirationsBlock
            loan={loan}
            trade={isTrade(readiness)}
            onPreviewDoc={onPreviewDoc}
            onDocsChanged={onDocsChanged}
          />
        )}
        <LockBlock readiness={readiness} />
        {!compact && <ActivityBlock readiness={readiness} />}
        <AiReviewBlock readiness={readiness} full={aiReviewFull} onOpen={onOpenAiReview} />
      </BlockGroup>
      {isTrade(readiness) && compact && (
        <Muted sx={{ display: "block", mt: 1 }}>Trade settlement — dates are owned by Capital Markets.</Muted>
      )}
    </Box>
  );
};

export default ReadinessDetail;
